import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, matchRoutes } from "react-router-dom";
import { type ReversalReason } from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import {
  useACHReverseSendPermission,
  useACHReverseCollectPermission,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments/permissions";
import {
  useResolvedBulkPayment,
  useCreateBulkReversal,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import {
  useFetchAllBulkPaymentPayments,
  formatPaymentActivity,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import { useAllRecipients } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import { useSudoContext } from "../../SudoContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import SudoDialog from "../../SudoDialog";
import BulkReversePaymentModal from "./BulkReversePaymentModal";

type BulkReverseFlowProps = {
  bulkPaymentId: string;
  // route this flow is mounted at (sudo returns here after OTP)
  reverseRoute: string;
  // where to go when the modal is dismissed before completing
  dismissRoute: string;
  // when set, reverse only this subset (from the selection screen); otherwise
  // reverse every eligible payment in the bulk
  selectedUuids?: string[];
  // when provided the flow skips fetching for payment activity
  preloadedPayments?: API.FormattedPaymentActivity[];
  onComplete?: () => void;
};

/**
 * url driven flow for the bulk reverse modal (including sudo). mirrors the
 * individual ReverseFlow: resolves the bulk + its eligible/selected payments,
 * gates on eligibility, runs the reversal optionally behind
 * sudo, then flips to the receipt.
 */
const BulkReverseFlow = ({
  bulkPaymentId,
  reverseRoute,
  dismissRoute,
  selectedUuids,
  preloadedPayments,
  onComplete,
}: BulkReverseFlowProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const institution = useInstitutionSettings();
  const recipients = useAllRecipients();
  const accounts = useAllAccounts();
  const { establishSudo, requestOtpRoute, submitOtpRoute } = useSudoContext();
  const { send: createBulkReversal } = useCreateBulkReversal();
  const [showReceipt, setShowReceipt] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [reversalDualApprovalId, setReversalDualApprovalId] =
    useState<API.DualApprovalId | null>(null);
  const hasReverseSendPermission = useACHReverseSendPermission();
  const hasReverseCollectPermission = useACHReverseCollectPermission();

  const bulkPayment = useResolvedBulkPayment({
    bulkPaymentId: bulkPaymentId as API.BulkPaymentId,
  });

  // only fetch when preloadedPayments is not provided
  // (i.e. "reverse all eligible" from the bulk table). loads every page so the
  // review covers the whole bulk
  const { payments: rawPayments } = useFetchAllBulkPaymentPayments(
    preloadedPayments === undefined ? bulkPaymentId : undefined
  );

  const selectedSet = useMemo(
    () => (selectedUuids ? new Set(selectedUuids) : null),
    [selectedUuids]
  );

  // the payments this reversal applies to. always filtered for reversibility,
  // selection, and the user's reverse permissions
  const payments = useMemo(() => {
    const source =
      preloadedPayments !== undefined
        ? preloadedPayments
        : (rawPayments ?? []).map((p) =>
            formatPaymentActivity(p, recipients, accounts)
          );

    return source.filter(
      (p) =>
        Boolean(p.isReversible) &&
        (!selectedSet || selectedSet.has(p.uuid)) &&
        (p.isCollectPayment
          ? hasReverseCollectPermission
          : hasReverseSendPermission)
    );
  }, [
    preloadedPayments,
    rawPayments,
    recipients,
    accounts,
    selectedSet,
    hasReverseCollectPermission,
    hasReverseSendPermission,
  ]);

  // preloadedPayments are available immediately; otherwise wait for the fetch
  const hasLoaded = preloadedPayments !== undefined || rawPayments !== null;
  const hasPayments = payments.length > 0;

  // mirrors the individual ReverseFlow eligibility gate: wait until data has
  // loaded, then redirect if the feature is off or there are no reversible
  // payments — a single effect avoids a premature redirect before load
  const isEligible =
    Boolean(institution.features?.user_initiated_ach_reversals) && hasPayments;

  useEffect(() => {
    if (!hasLoaded) return;
    if (!isEligible && !showReceipt) {
      navigate(dismissRoute, { replace: true });
    }
  }, [hasLoaded, isEligible, showReceipt, dismissRoute, navigate]);

  const isSudoRoute = Boolean(
    matchRoutes(
      [{ path: requestOtpRoute }, { path: submitOtpRoute }],
      location.pathname
    )
  );

  const sudoRequired = Boolean(
    institution.sudo_mode_required_for_user_initiated_ach_reversals
  );

  const updateSubmitting = (value: boolean) => {
    submittingRef.current = value;
    setSubmitting(value);
  };

  const executeReversal = async (reason: ReversalReason) => {
    if (!hasPayments) {
      updateSubmitting(false);
      return;
    }

    const transactionIds = payments.map((p) => p.uuid as API.ACHPaymentId);

    try {
      const bulkReversal = await createBulkReversal({
        bulkPaymentId: bulkPaymentId as API.BulkPaymentId,
        reversalReason: reason,
        transactionIds,
      });
      setReversalDualApprovalId(bulkReversal?.dual_approval_id ?? null);
    } catch {
      updateSubmitting(false);
      return;
    }

    setShowReceipt(true);
    updateSubmitting(false);
  };

  const handleReverse = (reason: ReversalReason) => {
    if (submittingRef.current) return;
    updateSubmitting(true);

    if (sudoRequired) {
      establishSudo(reverseRoute, () => executeReversal(reason)).catch(() => {
        // sudo cancelled / failed — stay on the form
        updateSubmitting(false);
      });
    } else {
      executeReversal(reason);
    }
  };

  const handleClose = () => {
    if (showReceipt && onComplete) {
      onComplete();
    } else {
      navigate(dismissRoute);
    }
  };

  return (
    <>
      <BulkReversePaymentModal
        isOpen={!isSudoRoute && (isEligible || showReceipt)}
        showReceipt={showReceipt}
        bulkPayment={bulkPayment}
        payments={payments ?? []}
        handleClose={handleClose}
        handleReverse={handleReverse}
        submitting={submitting}
        disableSubmit={!hasPayments}
        reversalDualApprovalId={reversalDualApprovalId}
      />
      <SudoDialog />
    </>
  );
};

export default BulkReverseFlow;
